/**
 * RSVPs service
 * Provides typed functions for interacting with the event_rsvps table
 */

import { supabase } from '../supabase';
import type {
  EventRsvp,
  EventRsvpInsert,
  EventRsvpUpdate,
  UserRole,
} from '../types/database';
import type { EventNotificationSubscription } from '../types/notifications.types';
import type { UpsertRsvpResult } from '../types/rsvps.types';

/**
 * Fetch all RSVPs for an event
 */
type EventRsvpViewRow = EventRsvp & {
  full_name: string | null;
  email: string | null;
  role: UserRole | null;
};

function hasActiveSubscriptionFlags(
  subscription: EventNotificationSubscription
) {
  return (
    subscription.remind_3_days ||
    subscription.remind_1_day ||
    subscription.remind_1_hr ||
    subscription.notify_changes
  );
}

async function syncOneDayReminderSubscriptions(rsvps: EventRsvp[]) {
  if (rsvps.length === 0) {
    return;
  }

  const userId = rsvps[0].user_id;
  const eventIds = rsvps
    .map((rsvp) => rsvp.event_id)
    .filter((eventId): eventId is number => eventId !== null);

  if (!userId || eventIds.length === 0) {
    return;
  }

  const { data: existingSubscriptions, error: subscriptionsError } =
    await supabase
      .from('event_notification_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .in('event_id', eventIds);

  if (subscriptionsError) {
    throw new Error(
      `Failed to fetch event subscriptions: ${subscriptionsError.message}`
    );
  }

  const subscriptionsByEventId = new Map(
    (
      (existingSubscriptions as EventNotificationSubscription[] | null) ?? []
    ).map((subscription) => [subscription.event_id, subscription])
  );

  const subscriptionsToUpsert = rsvps
    .filter(
      (rsvp): rsvp is EventRsvp & { event_id: number } => rsvp.event_id !== null
    )
    .filter((rsvp) => rsvp.status === 'yes')
    .map((rsvp) => {
      const existingSubscription = subscriptionsByEventId.get(rsvp.event_id);

      return {
        user_id: rsvp.user_id,
        event_id: rsvp.event_id,
        remind_3_days: existingSubscription?.remind_3_days ?? false,
        remind_1_day: true,
        remind_1_hr: existingSubscription?.remind_1_hr ?? false,
        notify_changes: existingSubscription?.notify_changes ?? false,
      };
    });

  if (subscriptionsToUpsert.length > 0) {
    const { error: upsertError } = await supabase
      .from('event_notification_subscriptions')
      .upsert(subscriptionsToUpsert, { onConflict: 'user_id,event_id' });

    if (upsertError) {
      throw new Error(
        `Failed to sync 1-day reminder subscriptions: ${upsertError.message}`
      );
    }
  }

  const subscriptionsToClear = rsvps
    .filter(
      (rsvp): rsvp is EventRsvp & { event_id: number } => rsvp.event_id !== null
    )
    .filter((rsvp) => rsvp.status !== 'yes')
    .map((rsvp) => subscriptionsByEventId.get(rsvp.event_id))
    .filter(
      (subscription): subscription is EventNotificationSubscription =>
        !!subscription?.remind_1_day
    );

  const subscriptionIdsToDelete = subscriptionsToClear
    .filter(
      (subscription) =>
        !hasActiveSubscriptionFlags({
          ...subscription,
          remind_1_day: false,
        })
    )
    .map((subscription) => subscription.id);

  if (subscriptionIdsToDelete.length > 0) {
    const { error: deleteError } = await supabase
      .from('event_notification_subscriptions')
      .delete()
      .in('id', subscriptionIdsToDelete);

    if (deleteError) {
      throw new Error(
        `Failed to delete empty event subscriptions: ${deleteError.message}`
      );
    }
  }

  const subscriptionIdsToUpdate = subscriptionsToClear
    .filter(
      (subscription) => !subscriptionIdsToDelete.includes(subscription.id)
    )
    .map((subscription) => subscription.id);

  if (subscriptionIdsToUpdate.length > 0) {
    const { error: updateError } = await supabase
      .from('event_notification_subscriptions')
      .update({ remind_1_day: false })
      .in('id', subscriptionIdsToUpdate);

    if (updateError) {
      throw new Error(
        `Failed to clear 1-day reminder subscriptions: ${updateError.message}`
      );
    }
  }
}

/**
 * Runs the 1-day reminder sync without letting it fail the RSVP.
 *
 * The RSVP row is already committed by the time this runs, so a reminder
 * outage must not be reported to the user as a failed RSVP. Returns true when
 * the sync succeeded.
 */
async function trySyncOneDayReminderSubscriptions(
  rsvps: EventRsvp[]
): Promise<boolean> {
  try {
    await syncOneDayReminderSubscriptions(rsvps);
    return true;
  } catch (error) {
    console.error('RSVP saved but 1-day reminder sync failed', error);
    return false;
  }
}

export async function getEventRsvps(eventId: number) {
  const { data, error } = await supabase
    .from('event_rsvps_with_profiles')
    .select('*')
    .eq('event_id', eventId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch RSVPs: ${error.message}`);
  }

  return (data as EventRsvpViewRow[]).map(
    ({ full_name, email, role, ...rsvp }) => ({
      ...rsvp,
      profile:
        full_name || email
          ? {
              full_name,
              email,
              role,
            }
          : null,
    })
  );
}

/**
 * Fetch RSVP for a specific user and event
 */
export async function getUserRsvp(eventId: number, userId: string) {
  const { data, error } = await supabase
    .from('event_rsvps')
    .select('*')
    .eq('event_id', eventId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch RSVP: ${error.message}`);
  }

  return data as EventRsvp | null;
}

/**
 * Fetch RSVPs for a user across multiple events in a single query.
 * Returns a Map of eventId -> EventRsvp for efficient lookup.
 */
export async function getUserRsvpsForEvents(
  eventIds: number[],
  userId: string
): Promise<Map<number, EventRsvp>> {
  if (eventIds.length === 0 || !userId) {
    return new Map();
  }

  const { data, error } = await supabase
    .from('event_rsvps')
    .select('*')
    .in('event_id', eventIds)
    .eq('user_id', userId);

  if (error) {
    throw new Error(`Failed to fetch RSVPs for events: ${error.message}`);
  }

  // Convert array to Map for O(1) lookup by eventId
  const rsvpMap = new Map<number, EventRsvp>();
  for (const rsvp of data as EventRsvp[]) {
    if (rsvp.event_id !== null) {
      rsvpMap.set(rsvp.event_id, rsvp);
    }
  }

  return rsvpMap;
}

/**
 * Create or update an RSVP
 * If the event is part of a series, creates RSVPs for all events in the series
 */
export async function upsertRsvp(
  rsvp: EventRsvpInsert
): Promise<UpsertRsvpResult> {
  // NOTE: If RSVP writes move outside the EventDetails page, this reminder sync
  // should become a backend invariant instead of being duplicated in clients.
  // First, check if this event is part of a series
  const { data: event, error: eventError } = await supabase
    .from('events')
    .select('series_id')
    .eq('id', rsvp.event_id)
    .single();

  if (eventError) {
    throw new Error(`Failed to fetch event: ${eventError.message}`);
  }

  if (event?.series_id) {
    // Get all events in the series.
    //
    // This is RLS-filtered, so occurrences the user cannot view never enter the
    // upsert below. It is NOT date-filtered though: past occurrences are
    // included. That matters because the upsert is a single statement — if any
    // row is rejected, the whole RSVP aborts. Today nothing writes
    // event_rsvps.status = 'attended' (attendance lives in event_attendance),
    // but legacy 'attended' rows on a past occurrence would trip
    // prevent_user_attended_status_change and fail the entire series RSVP.
    const { data: seriesEvents, error: seriesError } = await supabase
      .from('events')
      .select('id')
      .eq('series_id', event.series_id);

    if (seriesError) {
      throw new Error(`Failed to fetch series events: ${seriesError.message}`);
    }

    // Create RSVPs for all events in the series
    const rsvps: EventRsvpInsert[] =
      seriesEvents?.map((e) => ({
        event_id: e.id,
        user_id: rsvp.user_id,
        status: rsvp.status,
      })) || [];

    const { data, error } = await supabase
      .from('event_rsvps')
      .upsert(rsvps, { onConflict: 'event_id,user_id' })
      .select();

    if (error) {
      throw new Error(`Failed to upsert series RSVPs: ${error.message}`);
    }

    const allRsvps = data as EventRsvp[];
    const reminderSynced = await trySyncOneDayReminderSubscriptions(allRsvps);
    const requestedRsvp =
      allRsvps.find((r) => r.event_id === rsvp.event_id) || allRsvps[0];

    return {
      rsvp: requestedRsvp,
      allRsvps,
      seriesId: event.series_id,
      reminderSyncFailed: !reminderSynced,
    };
  } else {
    // Single event - normal RSVP
    const { data, error } = await supabase
      .from('event_rsvps')
      .upsert(rsvp, { onConflict: 'event_id,user_id' })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to upsert RSVP: ${error.message}`);
    }

    const singleRsvp = data as EventRsvp;
    const reminderSynced = await trySyncOneDayReminderSubscriptions([
      singleRsvp,
    ]);
    return {
      rsvp: singleRsvp,
      allRsvps: [singleRsvp],
      seriesId: null,
      reminderSyncFailed: !reminderSynced,
    };
  }
}

/**
 * Update an existing RSVP
 */
export async function updateRsvp(id: number, updates: EventRsvpUpdate) {
  const { data, error } = await supabase
    .from('event_rsvps')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update RSVP: ${error.message}`);
  }

  return data as EventRsvp;
}

/**
 * Delete an RSVP
 * Returns the deleted RSVP data for cache invalidation
 */
export async function deleteRsvp(id: number) {
  // First fetch the RSVP to get event_id and user_id for cache invalidation
  const { data: rsvp, error: fetchError } = await supabase
    .from('event_rsvps')
    .select('event_id, user_id')
    .eq('id', id)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch RSVP for deletion: ${fetchError.message}`);
  }

  // Delete the RSVP
  const { error } = await supabase.from('event_rsvps').delete().eq('id', id);

  if (error) {
    throw new Error(`Failed to delete RSVP: ${error.message}`);
  }

  // Return the event_id and user_id for cache invalidation
  return { event_id: rsvp.event_id, user_id: rsvp.user_id };
}
